import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { wheelService } from '../../services/wheelService';

const STATE_LABEL: Record<string, { label: string; color: 'primary' | 'secondary' | 'default' }> = {
  selling_puts: { label: 'Selling PUTs', color: 'primary' },
  selling_calls: { label: 'Selling CALLs', color: 'secondary' },
  closed: { label: 'Closed', color: 'default' },
};

const WheelList: React.FC = () => {
  const [strategies, setStrategies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    wheelService.listStrategies()
      .then(setStrategies)
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Wheel Strategies</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/discover?segment=wheel')}>
          New Strategy
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!strategies.length ? (
        <Alert severity="info">No active wheel strategies. Go to Discover &rarr; Wheel to start one.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                <TableCell>Ticker</TableCell>
                <TableCell>State</TableCell>
                <TableCell align="right">Open Positions</TableCell>
                <TableCell align="right">Total Trades</TableCell>
                <TableCell align="right">Total P&L</TableCell>
                <TableCell>Started</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {strategies.map((s: any) => {
                const stateInfo = STATE_LABEL[s.state] ?? STATE_LABEL.closed;
                const pnl = parseFloat(s.total_pnl || 0);
                return (
                  <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/portfolio/wheel/${s.id}`)}>
                    <TableCell><Typography fontWeight={700}>{s.ticker}</Typography></TableCell>
                    <TableCell><Chip label={stateInfo.label} color={stateInfo.color} size="small" /></TableCell>
                    <TableCell align="right">{s.open_trade_count}</TableCell>
                    <TableCell align="right">{s.total_trade_count}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 600, color: pnl >= 0 ? 'success.main' : 'error.main' }}>
                        {pnl >= 0 ? '+' : ''}${pnl.toFixed(0)}
                      </Typography>
                    </TableCell>
                    <TableCell>{s.created_at?.split('T')[0]}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default WheelList;
